// hooks/use-i18n-initializer.ts
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment/min/moment-with-locales';

import localStorageUtil from 'src/utils/localStorageUtil';

import i18n, { getLanguage } from 'src/i18n';
// Fallback 리소스 import
import koFallback from 'src/locales/fallback/ko.json';
import enFallback from 'src/locales/fallback/en.json';

// Fallback 함수
const getFallback = (languageCode: string) => {
  const code = languageCode.toLowerCase();
  console.log(
    `[i18n-hook] getFallback called with languageCode: ${languageCode} (normalized: ${code})`
  );

  if (code.startsWith('ko')) {
    console.log('[i18n-hook] Using Korean fallback');
    return koFallback;
  }
  console.log('[i18n-hook] Using English fallback (default)');
  return enFallback;
};

export default function useI18nInitializer(brandCode: string | null) {
  const location = useLocation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const initializeFallback = async () => {
      console.log('[i18n-hook] brandCode 없음, fallback 초기화 시작');
      const browserLang = navigator.language || 'en';
      let langCode = 'en';
      if (browserLang.toLowerCase().startsWith('ko')) {
        langCode = 'ko';
      }

      try {
        // 직접 fallback 메시지 사용
        const messages = getFallback(langCode);
        console.log(`[i18n-hook] Adding fallback resources for: ${langCode}`);

        // 기존 리소스 제거 후 새로운 리소스 추가
        i18n.removeResourceBundle(langCode, 'translation');
        i18n.addResources(langCode, 'translation', messages);

        // 언어 변경
        await i18n.changeLanguage(langCode);

        // DOM 설정
        document.documentElement.setAttribute('lang', langCode);
        document.body.setAttribute('dir', 'ltr');
        moment.locale(langCode);

        const languageData = JSON.stringify({
          code: langCode,
          staticFileUrl: 'fallback',
          rtl: false,
        });
        localStorageUtil.setLanguageInfo(languageData);

        console.log(`[i18n-hook] Fallback initialization completed for: ${langCode}`);
        setReady(true);
      } catch (error) {
        console.error('[i18n-hook] Fallback initialization failed:', error);
        setReady(false);
      }
    };

    const init = async () => {
      const current = i18n.getResourceBundle(i18n.language, 'translation');
      const isLoaded = current && Object.keys(current).length > 0;

      // brandCode 없을 때만 기존 리소스 사용
      if (!brandCode) {
        if (isLoaded) {
          setReady(true);
          return;
        }
        await initializeFallback();
        return;
      }

      const ok = await getLanguage(brandCode);
      setReady(ok);
    };

    init();
  }, [brandCode, location.pathname]);

  return { ready };
}
